import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { SelectComponent } from '@blaze-shared/shared-feature';
import { ButtonComponent, ButtonIconComponent, DialogComponent } from '@blaze-shared/shared-ui';
import { SelectOption } from '@blaze-shared/shared-util';

import {
  ApiBuilderParameterExtended,
  BodyParameterEditDialogData,
  BodyParameterEditForm,
  FormParameterDataType,
  ParameterPaginationMethod,
  ParameterType,
} from '../api-builder.model';
import { ApiBuilderService } from '../services/api-builder.service';
import { TypedValueInputFieldComponent } from '../typed-value-input-field/typed-value-input-field.component';

@UntilDestroy()
@Component({
  selector: 'blaze-body-parameter-edit-dialog',
  imports: [
    ButtonComponent,
    ButtonIconComponent,
    DialogComponent,
    FormsModule,
    MatButtonToggleModule,
    MatIconModule,
    MatSlideToggleModule,
    ReactiveFormsModule,
    SelectComponent,
    TypedValueInputFieldComponent,
  ],
  templateUrl: './body-parameter-edit-dialog.component.html',
  styleUrls: ['./body-parameter-edit-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BodyParameterEditDialogComponent implements OnInit {
  private readonly dialogRef =
    inject<
      MatDialogRef<
        BodyParameterEditDialogComponent,
        Partial<ApiBuilderParameterExtended> | undefined
      >
    >(MatDialogRef);
  private readonly apiBuilderService = inject(ApiBuilderService);

  formCtrl!: FormGroup<BodyParameterEditForm>;
  parameter: ApiBuilderParameterExtended;
  readonly paginationMethods: SelectOption<ParameterPaginationMethod>[];
  readonly dataTypes: SelectOption<FormParameterDataType>[];

  readonly ParameterType = ParameterType;
  readonly ParameterPaginationMethod = ParameterPaginationMethod;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formValue() {
    return this.formCtrl.value;
  }

  get showDefaultValueField(): boolean {
    return (
      !!this.formValue.optional &&
      this.formValue.type !== ParameterType.Pagination &&
      this.formValue.dataType !== 'auto' &&
      this.formValue.dataType !== 'array' &&
      this.formValue.dataType !== 'file'
    );
  }

  constructor() {
    const dialogData = inject<BodyParameterEditDialogData>(MAT_DIALOG_DATA);

    this.parameter = dialogData.parameter;
    this.paginationMethods = [
      { value: ParameterPaginationMethod.PageNumber, label: 'Page Number' },
      { value: ParameterPaginationMethod.PageSize, label: 'Page Size' },
      { value: ParameterPaginationMethod.RowOffset, label: 'Row Offset' },
    ];
    this.dataTypes = [
      { value: 'auto', label: 'Auto' },
      { value: 'boolean', label: 'Boolean' },
      { value: 'datetime', label: 'DateTime' },
      { value: 'date', label: 'Date' },
      { value: 'number', label: 'Number' },
      { value: 'string', label: 'String' },
      { value: 'array', label: 'Array' },
      { value: 'file', label: 'File' },
      { value: 'time', label: 'Time' },
    ];
  }

  ngOnInit(): void {
    const parameterType =
      this.parameter.type !== ParameterType.FixedValue
        ? this.parameter.type
        : ParameterType.Dynamic;

    this.formCtrl = new FormGroup({
      name: new FormControl(this.parameter.name, {
        nonNullable: true,
        validators: Validators.required,
      }),
      type: new FormControl<ParameterType>(parameterType, { nonNullable: true }),
      optional: new FormControl(!!this.parameter.optional, { nonNullable: true }),
      paginationMethod: new FormControl(
        this.parameter.paginationMethod || ParameterPaginationMethod.PageNumber,
        { nonNullable: true },
      ),
      dataType: new FormControl(this.parameter.dataType || 'auto', { nonNullable: true }),
      defaultValue: new FormControl(
        this.apiBuilderService.serializeParameterDefaultValue(this.parameter.defaultValue ?? ''),
        { nonNullable: true },
      ),
    });

    this.formCtrl.controls.dataType.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.formCtrl.controls.defaultValue.setValue('');
    });
  }

  save(): void {
    if (this.formCtrl.invalid) {
      return;
    }
    const result = this.getParsedFormValue();
    this.dialogRef.close(result);
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  private getParsedFormValue(): Partial<ApiBuilderParameterExtended> {
    const { name, type, paginationMethod, optional, dataType, defaultValue } = this.formCtrl.value;

    const parsedDataType = dataType !== 'auto' ? dataType : undefined;

    const parsedDefaultValue =
      optional && defaultValue && parsedDataType
        ? this.apiBuilderService.deserializeParameterDefaultValue(defaultValue, parsedDataType)
        : undefined;

    return {
      name,
      type,
      paginationMethod,
      optional,
      dataType: parsedDataType,
      defaultValue: parsedDefaultValue,
    };
  }
}
